import { useContext, useState, useEffect } from "react";
import { SecurePaymentsDisclaimer } from "~/ui/components/DisclaimerContainer";
import { SubscriptionCheckout } from "~/ui/components/StripeCheckout";
import { darkTheme, styled } from "~/ui/style/stitches.config";
import { AuthContext } from "~/utils/useAuth";
import { useSnapshot } from "valtio";
import { localState } from "~/state/state";
import { t } from "~/localization/translate";
import { formatCurrency } from "~/localization/formatPrice";
import type { PlusPrices } from "~/api/paymentsApi";
import { SimpleLoadingIndicator } from "~/pages/loading/LoadingPage";
import { Button } from "~/ui/components/Button";
export const BillingOverlay = ({
  onSuccess,
  billingPeriod,
  plusPrices,
  plusPricesError,
  onRetryPrices,
}: {
  onSuccess: (subscriptionId: string) => void;
  billingPeriod: 'monthly' | 'annual';
  plusPrices?: PlusPrices | null;
  plusPricesError?: boolean;
  onRetryPrices?: () => void;
}) => {
  const [isSubmittingPayment, setIsSubmittingPayment] = useState(false);
  const { user } = useContext(AuthContext);
  const { isOnMobile } = useSnapshot(localState);

  const price = plusPrices
    ? billingPeriod === 'monthly'
      ? plusPrices.monthly
      : plusPrices.annual
    : null;
  const amount = price?.unitAmount ?? 0; // cents
  const currency = price?.currency ?? "usd";
  const [discountedPrice, setDiscountedPrice] = useState(amount);

  // Keep the (non-discounted) displayed/charged amount in sync once prices load.
  useEffect(() => {
    setDiscountedPrice(amount);
  }, [amount]);

  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, []);

  return (
    <StyledBillingOverlay>
      <CloseButton>
        <img
          src="/images/room/x-icon.png"
          className="x-button"
          onClick={(e) => {
            const closeEvent = new CustomEvent("closeBillingModal");
            document.dispatchEvent(closeEvent);
            e.stopPropagation();
          }}
        />
      </CloseButton>
      <div
        style={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
          flexDirection: "column",
        }}
      >
        {!price && plusPricesError ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "16px",
              margin: "40px auto",
              textAlign: "center",
            }}
          >
            <div style={{ fontSize: "14px" }}>{t("overlay.loadError")}</div>
            {onRetryPrices && (
              <Button onClick={() => onRetryPrices()}>
                {t("overlay.retry")}
              </Button>
            )}
          </div>
        ) : !price ? (
          <SimpleLoadingIndicator style={{ margin: "40px auto" }} />
        ) : (
        <>
        <div className="top-container">
          <div className="title">{billingPeriod === 'monthly' ? t("overlay.titleMonthly") : t("overlay.titleAnnual")}</div>
          <div className="subtitle">{t("overlay.autoRenews")}</div>
          <div className="total-line">
            <div className="total-price">
              {discountedPrice !== amount ? (
                <>
                  <span
                    style={{
                      textDecoration: "line-through",
                      color: "#888",
                      marginRight: "8px",
                    }}
                  >
                    {formatCurrency(amount, currency)}
                  </span>
                  <span style={{ color: "var(--colors-text)" }}>
                    {formatCurrency(discountedPrice, currency)}
                  </span>
                </>
              ) : (
                formatCurrency(amount, currency)
              )} / {billingPeriod === 'monthly' ? t("overlay.perMonth") : t("overlay.perYear")}
            </div>
          </div>
        </div>

        <SubscriptionCheckout
          useremail={user?.email || ""}
          amount={amount}
          currency={currency}
          metadata={{
            requesterId: user?.uuid || "",
            requesterUsername: user?.username || "",
          }}
          buttonStyle={{
            backgroundColor: "#efc1ff",
            fontSize: "15px",
            height: "52px",
            marginTop: "24px",
          }}
          isSubmittingPayment={isSubmittingPayment}
          setIsSubmittingPayment={setIsSubmittingPayment}
          onSuccess={onSuccess}
          discountedPrice={discountedPrice}
          setDiscountedPrice={setDiscountedPrice}
          billingPeriod={billingPeriod}
        />
        </>
        )}
      </div>

      <SecurePaymentsDisclaimer
        text=""
        style={{
          fontSize: "11px",
          textWrap: isOnMobile ? "wrap" : "nowrap",
          marginTop: "10px",
          marginBottom: isOnMobile ? "50px" : "0px",
        }}
      />
    </StyledBillingOverlay>
  );
};




const CloseButton = styled("div", {
  // Base styles that apply to all screen sizes
  display: "flex",
  justifyContent: "flex-end",
  alignItems: "center",
  position: "absolute",
  top: "20px",
  right: "20px",
  width: "20px",
  height: "20px",
  zIndex: 99999999,
  
  // Mobile-specific styles
  "@mobile": {
    position: "fixed",
    top: 46,
    left: 10,
    width: "100%",
    height: "56px",
    backgroundColor: "$wash",
    [`.${darkTheme.className} &`]: {
      backgroundColor: "$appBg",
    },

    "& .x-button": {
      width: "20px",
      height: "20px",
      filter: "invert(1)",
      [`.${darkTheme.className} &`]: {
        filter: "none",
      },
      cursor: "pointer",
      opacity: "0.8",
      marginRight: "20px",
      transform: "scale(1.4)",
    },
  },
});


const StyledBillingOverlay = styled("div", {
  width: "500px",
  zIndex: "99999999!important",
  backgroundColor: "$wash",
  color: "$text",
  padding: "70px 60px",
  border: "1px solid black",
  borderRadius: "33px",
  position: "fixed",
  [`.${darkTheme.className} &`]: {
    backgroundColor: "$appBg",
    border: "1px solid $border",
  },
  "& .x-button": {
    position: "absolute",
    top: "20px",
    right: "20px",
    width: "20px",
    height: "20px",
    filter: "invert(1)",
    [`.${darkTheme.className} &`]: {
      filter: "none",
    },
    cursor: "pointer",
    opacity: "0.8",
    "&:hover": {
      opacity: "0.5",
    },
    zIndex: 99999999,
  },
  maxHeight: "90vh",
  overflowY: "scroll",
  "&::-webkit-scrollbar": {
    webkitAppearance: "none",
    width: "8px",
    marginLeft: "1px",
  },
  "&::-webkit-scrollbar-thumb": {
    borderRadius: "20px",
    backgroundColor: "rgba(0, 0, 0, .5)",
    border: "1px solid $wash",
    [`.${darkTheme.className} &`]: {
      backgroundColor: "rgba(255, 255, 255, .35)",
      border: "1px solid $appBg",
    },
  },
  "&::-webkit-scrollbar-track": {
    overflow: "hidden",
    marginTop: "60px",
    marginBottom: "30px",
    width: "8px",
  },
  "& .top-container": {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    textAlign: "left",
    width: "100%",
    "& .title": {
      fontSize: "15px",
      "@mobile": {
        fontSize: "16px",
      },
      fontWeight: "500",
    },
    "& .subtitle": {
      fontSize: "13px",
      "@mobile": {
        fontSize: "14px",
        marginBottom: "10px",
      },
      marginTop: "3px",
      width: "101%",
    },
    "& .total-line": {
      display: "flex",
      justifyContent: "space-between",
      width: "100%",
      marginTop: "20px",
      marginBottom: "30px",
      fontSize: "18px",

      "& .total-price": {
        fontWeight: "400",
      },
    },
  },
  "@mobile": {
    width: "100vw",
    height: "calc(100vh - 50px)",
    padding: "70px 30px 200px 30px",
    maxHeight: "none",
    margin: 0,
    borderRadius: 0,
    border: "none",
    overflowY: "auto", 
    overflowX: "hidden",
    position: "fixed !important",
    top: "50px", 
    left: 0,
    zIndex: 99999999,
  },
});

